/**
 * The guided shot list (FR1–FR3).
 *
 * The list is derived from the point of impact, not fixed: a front-left hit
 * needs different photographs from a hail claim. This is the step that decides
 * whether the AI has anything worth reading, so it is enforced rather than
 * suggested — capture quality is the whole premise.
 *
 * Pure. The shop can extend this later (FR8); this is the shipped default.
 */

export type ImpactPoint =
  | 'front' | 'front_left' | 'front_right'
  | 'rear' | 'rear_left' | 'rear_right'
  | 'left' | 'right'
  | 'all_over' | 'roll_over' | 'non_collision';

export type ShotStatus = 'todo' | 'taken' | 'retake';

/** Which orthographic view the reference drawing uses. */
export type View = 'front' | 'side' | 'top' | 'rear';

export interface ShotSpec {
  id: string;
  title: string;
  /** What to frame, in the estimator's words. */
  guidance: string;
  view: View;
  /** Where to stand, shown on the reference drawing. */
  angle: string;
  /**
   * A shot the AI cannot work without. A missing required shot blocks any
   * proposal about the zones it covers.
   */
  required: boolean;
  /** Zones this photograph can actually show. Drives the gate. */
  covers: string[];
}

/** Shots every job needs, whatever was hit. */
const ALWAYS: ShotSpec[] = [
  { id: 'VIN', title: 'VIN plate', guidance: 'Readable, no glare.', view: 'side',
    angle: 'CLOSE, SQUARE ON', required: true, covers: [] },
  { id: 'PAINT', title: 'Paint code', guidance: 'Door jamb sticker.', view: 'side',
    angle: 'DOOR OPEN', required: true, covers: [] },
  { id: 'WIDE_L', title: 'Full vehicle, left', guidance:
      'Whole vehicle side on. Establishes what was already damaged.',
    view: 'side', angle: 'SIDE ON, WHOLE CAR', required: true, covers: ['prior_damage'] },
];

const FRONT_CORE: ShotSpec[] = [
  { id: 'F_BUMPER', title: 'Bumper, square on', guidance:
      'Directly in front, camera at bumper height. Angled shots hide how far the deformation runs.',
    view: 'front', angle: 'SQUARE ON, BUMPER HEIGHT', required: true,
    covers: ['front_bumper', 'grille'] },
  { id: 'ENGINE_BAY', title: 'Engine bay', guidance:
      'Hood open, standing square at the front. Without it nothing behind the bumper can be judged.',
    view: 'top', angle: 'HOOD OPEN', required: true,
    covers: ['radiator_support', 'cooling', 'engine_bay'] },
  { id: 'HOOD', title: 'Hood, raking', guidance:
      'Shoot along the panel, not at it. A raking angle is the only way depth reads in a photo.',
    view: 'side', angle: 'RAKING ALONG THE PANEL', required: true, covers: ['hood'] },
];

function corner(side: 'left' | 'right', end: 'front' | 'rear'): ShotSpec[] {
  const S = side === 'left' ? 'LH' : 'RH';
  const zone = `${end}_${side}`;
  return [
    { id: `Q_${S}_${end.toUpperCase()}`, title: `3/4 ${end} ${side}`,
      guidance: `Two paces back, off the damaged corner. Both faces in one frame.`,
      view: 'front', angle: 'TWO PACES BACK, OFF THE CORNER', required: true,
      covers: [zone, `${end}_bumper`, `${side}_fender`] },
    { id: `LAMP_${S}`, title: `${S} ${end === 'front' ? 'headlamp' : 'tail lamp'}`,
      guidance: 'Close enough to read the mounting tabs. Tabs decide repair versus replace.',
      view: 'front', angle: 'FILL THE FRAME', required: true,
      covers: [`${side}_${end === 'front' ? 'headlamp' : 'taillamp'}`] },
    { id: `PANEL_${S}`, title: `${S} ${end === 'front' ? 'fender' : 'quarter'}`,
      guidance: 'Raking angle along the panel so the crease shows its depth.',
      view: 'side', angle: 'RAKING ALONG THE PANEL', required: true,
      covers: [`${side}_${end === 'front' ? 'fender' : 'quarter'}`] },
  ];
}

/** Build the list for a point of impact. */
export function shotListFor(impact: ImpactPoint): ShotSpec[] {
  const out: ShotSpec[] = [...ALWAYS];

  switch (impact) {
    case 'front':
      out.push(...FRONT_CORE, ...corner('left', 'front'), ...corner('right', 'front'));
      break;
    case 'front_left':
      out.push(...FRONT_CORE, ...corner('left', 'front'));
      break;
    case 'front_right':
      out.push(...FRONT_CORE, ...corner('right', 'front'));
      break;
    case 'rear':
      out.push(...corner('left', 'rear'), ...corner('right', 'rear'));
      break;
    case 'rear_left':
      out.push(...corner('left', 'rear'));
      break;
    case 'rear_right':
      out.push(...corner('right', 'rear'));
      break;
    case 'left':
    case 'right': {
      const side = impact;
      out.push(
        { id: `SIDE_${side}`, title: `${side} side, full length`,
          guidance: 'Raking along the whole side so every panel gap reads.',
          view: 'side', angle: 'RAKING, WHOLE SIDE', required: true,
          covers: [`${side}_fender`, `${side}_front_door`, `${side}_rear_door`, `${side}_quarter`] },
        { id: `SILL_${side}`, title: `${side} sill and arches`,
          guidance: 'Low, along the sill. Arch lips are where side hits show.',
          view: 'side', angle: 'LOW, ALONG THE SILL', required: true,
          covers: [`${side}_sill`, `${side}_arches`] },
      );
      break;
    }
    case 'all_over':
      // Hail and storm work: coverage beats close-ups, and a raking light is
      // the only way dents on a horizontal panel show at all.
      out.push(
        { id: 'ROOF', title: 'Roof, raking', guidance:
            'Stand high and shoot across the panel. Dents vanish in flat light.',
          view: 'top', angle: 'HIGH, RAKING ACROSS', required: true, covers: ['roof'] },
        { id: 'HOOD_H', title: 'Hood, raking', guidance: 'Across the panel, not at it.',
          view: 'top', angle: 'RAKING ACROSS', required: true, covers: ['hood'] },
        { id: 'DECK', title: 'Boot lid or bed, raking', guidance: 'Across the panel.',
          view: 'top', angle: 'RAKING ACROSS', required: true, covers: ['deck', 'bed'] },
        { id: 'WIDE_R', title: 'Full vehicle, right', guidance: 'Whole vehicle, other side.',
          view: 'side', angle: 'SIDE ON, WHOLE CAR', required: true, covers: ['prior_damage'] },
      );
      break;
    case 'roll_over':
      out.push(
        { id: 'ROOF', title: 'Roof and pillars', guidance: 'Roof line and every pillar.',
          view: 'top', angle: 'HIGH, WHOLE ROOF', required: true,
          covers: ['roof', 'a_pillar', 'b_pillar', 'c_pillar'] },
        { id: 'WIDE_R', title: 'Full vehicle, right', guidance: 'Whole vehicle, other side.',
          view: 'side', angle: 'SIDE ON, WHOLE CAR', required: true, covers: ['prior_damage'] },
      );
      break;
    case 'non_collision':
      // No impact to reason from, so the list is only the always-shots plus
      // whatever the estimator adds by hand.
      break;
  }

  // De-duplicate by id, keeping the first. A front hit adds HOOD once even
  // though both corners want it.
  const seen = new Set<string>();
  return out.filter((s) => (seen.has(s.id) ? false : (seen.add(s.id), true)));
}

/**
 * A shot the MODEL asked for, rather than one the impact point implies.
 *
 * Every `cannotAssess` entry names the photograph that would settle it. Before
 * this, that request was a sentence in a list — the estimator could read
 * "wants: underbody photograph with the bumper cover removed" and had nowhere
 * to put it. These become real cards in Capture, so the app's own question can
 * be answered inside the app.
 *
 * The id is derived from the part label, so the same blind spot always maps to
 * the same slot: photograph it once, and the card fills rather than duplicating.
 */
export interface ExtraShot {
  id: string;
  title: string;
  guidance: string;
}

/**
 * `rear bumper impact bar` -> `ASK_REAR_BUMPER_IMPACT_BAR`.
 *
 * Stable, because the id is the storage slot: photograph the part once and the
 * card fills rather than a second one appearing beside it.
 *
 * The hash suffix is not decoration. Two real labels — "...apron and radiator
 * support bracket" and "...apron and radiator core support" — are identical in
 * their first 40 characters, so a plain truncation gave them ONE slot and the
 * second photograph would silently overwrite the first. A test caught it.
 */
export function extraShotId(partLabel: string): string {
  const slug = partLabel.toUpperCase().replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) return 'ASK_PART';
  if (slug.length <= 40) return `ASK_${slug}`;

  // FNV-1a over the WHOLE label, so the part that was cut off still decides
  // the id. Base36 keeps it short and slot-safe.
  let h = 0x811c9dc5;
  for (let i = 0; i < slug.length; i++) {
    h ^= slug.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `ASK_${slug.slice(0, 34)}_${h.toString(36).toUpperCase()}`;
}

/**
 * Turn the model's unseen-part list into shot cards.
 *
 * Items already photographed still get a card — it shows as captured, which is
 * how the estimator sees that the question has been answered.
 */
export function extraShotsFor(
  items: Array<{ partLabel: string; photographWanted: string }>,
): ExtraShot[] {
  const seen = new Set<string>();
  const out: ExtraShot[] = [];
  for (const it of items) {
    const id = extraShotId(it.partLabel);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, title: it.partLabel, guidance: it.photographWanted });
  }
  return out;
}

export interface ShotState {
  id: string;
  status: ShotStatus;
  /** Why a retake was asked for, shown on the card. */
  reason?: string;
  /**
   * A model has already read this photograph.
   *
   * The bytes cannot change, so reading it again buys nothing and spends the
   * quota twice. The button counts only unread photographs, and a retake
   * clears the stamp because the bytes really did change.
   */
  analysed?: boolean;
}

export interface CaptureGaps {
  /** Recommended shots not yet usable. NOT a barrier — see `canProceed`. */
  missing: ShotSpec[];
  /** Zones no usable photograph covers. Proposals about these are gated. */
  uncoveredZones: Set<string>;
  /** Every recommended shot is in. */
  complete: boolean;
  /** How many usable photographs exist. */
  taken: number;
  /**
   * Whether the estimator may go on to Review.
   *
   * **One photograph is enough.** The shot list is what would let the model
   * judge the MOST; it is not a checklist to be completed before anything can
   * happen. A rear-end shunt that creased one bumper needs one photograph, and
   * demanding nine to look at it would teach estimators that the app wastes
   * their time — which is the fastest way to lose them.
   *
   * The honest limit is enforced elsewhere and precisely: a proposal about a
   * zone nobody photographed is blocked by the gate. Fewer photographs simply
   * mean fewer things the model may claim, which is the correct trade and one
   * the estimator can see.
   */
  canProceed: boolean;
}

/**
 * What is still missing, and which zones therefore cannot be judged.
 *
 * A `retake` counts as missing: a photograph shot flat-on when a raking angle
 * was asked for is worse than none, because it invites a confident wrong answer.
 */
export function captureGaps(list: ShotSpec[], states: ShotState[]): CaptureGaps {
  const byId = new Map(states.map((s) => [s.id, s]));
  const missing = list.filter((s) => {
    if (!s.required) return false;
    const st = byId.get(s.id);
    return !st || st.status !== 'taken';
  });

  const covered = new Set<string>();
  for (const spec of list) {
    if (byId.get(spec.id)?.status === 'taken') {
      for (const z of spec.covers) covered.add(z);
    }
  }
  const uncoveredZones = new Set<string>();
  for (const spec of list) {
    for (const z of spec.covers) if (!covered.has(z)) uncoveredZones.add(z);
  }

  const taken = list.filter((s) => byId.get(s.id)?.status === 'taken').length;

  return {
    missing,
    uncoveredZones,
    complete: missing.length === 0,
    taken,
    canProceed: taken > 0,
  };
}
